import { MessageSquareIcon, SparkleIcon } from 'lucide-react';
import { useId } from 'react';
import { cn } from '../../../lib/utils.ts';

type Props = {
    className?: string;
    gradient?: boolean;
};

/**
 * The entry-point mark for Ask Plato: a lucide chat bubble carrying the AI
 * sparkle (the main star of `AiIcon`, as lucide's `Sparkle`).
 */
export function AiChatIcon({ className, gradient = false }: Props) {
    // useId can emit characters that break `url(#…)` references — strip them.
    const gradientId = `ai-chat-icon-${useId().replaceAll(/[^a-zA-Z0-9-]/g, '')}`;
    const stroke = gradient ? `url(#${gradientId})` : 'currentColor';

    return (
        <span aria-hidden="true" className={cn('relative inline-block h-4 w-4', className)}>
            {gradient && (
                <svg width="0" height="0" className="absolute" focusable="false">
                    <defs>
                        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0" stopColor="var(--color-ai-1)" />
                            <stop offset="1" stopColor="var(--color-ai-2)" />
                        </linearGradient>
                    </defs>
                </svg>
            )}
            <MessageSquareIcon stroke={stroke} className="h-full w-full" />
            <SparkleIcon
                stroke={stroke}
                // Rendered at half size, so 4 units keep the bubble's optical 2-unit stroke.
                strokeWidth={4}
                // Centred on the bubble body — (12, 11) in lucide's 24px grid; the
                // square spans y 3-19, so its middle sits at 46%, not 50%.
                className="absolute left-1/2 top-[46%] h-1/2 w-1/2 -translate-x-1/2 -translate-y-1/2"
            />
        </span>
    );
}
